import { Badge, COLOR, HIERARCHY, SHAPE } from "baseui/badge";
import { styled } from "baseui";
import { useIntl } from "react-intl";
import { ISubscription, SubscriptionStatus } from "@oqdo/automationapps/Memberships/Subscription";
import { addHours } from "date-fns";

const SubscriptionStatusView: React.FC<ISubscription> = (props) => {
  return <DataTemplate {...props} />;
};

export default SubscriptionStatusView;

const DataTemplate: React.FC<ISubscription> = (subscription) => {
  const intl = useIntl();
  const statusColor = subscription ? statusColors[subscription.status] : COLOR.primary;

  const isCancel = subscription.cancelAt ? new Date(subscription.cancelAt) > new Date() : false;
  const statusHierarchy = subscription ? statusHierarchies[subscription.status] : HIERARCHY.primary;

  const hasFutureStartTime = subscription.startTime && new Date(subscription.startTime) > addHours(new Date(), 6);
  switch (subscription.status) {
    case SubscriptionStatus.canceled:
      if (isCancel)
        return (
          <Badge
            content={
              <>
                {intl.formatMessage({
                  id: `StripeSubscriptionStatus.${SubscriptionStatus[subscription.status]}`,
                  defaultMessage: SubscriptionStatus[subscription.status],
                })}{" "}
                {isCancel && <>{new Date(subscription.canceledAt ?? new Date()).toLocaleDateString()}</>}
              </>
            }
            hierarchy={statusHierarchy}
            shape={SHAPE.pill}
            color={statusColor}
          />
        );
      else
        return (
          <Badge
            content={
              <>
                {intl.formatMessage({
                  id: `StripeSubscriptionStatus.${SubscriptionStatus[subscription.status]}`,
                  defaultMessage: SubscriptionStatus[subscription.status],
                })}{" "}
                <>{subscription.cancelAt && <> {new Date(subscription.cancelAt).toLocaleDateString()}</>}</>
              </>
            }
            hierarchy={statusHierarchy}
            shape={SHAPE.pill}
            color={statusColor}
          />
        );
    case SubscriptionStatus.trialing:
      return (
        <Badge
          content={
            <>
              {intl.formatMessage({
                id: `StripeSubscriptionStatus.${SubscriptionStatus[subscription.status]}`,
                defaultMessage: SubscriptionStatus[subscription.status],
              })}
              {subscription.trialEndTime && <> {new Date(subscription.trialEndTime).toLocaleDateString()}</>}
            </>
          }
          hierarchy={statusHierarchy}
          shape={SHAPE.pill}
          color={statusColor}
        />
      );
    case SubscriptionStatus.not_started:
      return (
        <Badge
          content={
            <>
              {intl.formatMessage({
                id: `StripeSubscriptionStatus.${SubscriptionStatus[subscription.status]}`,
                defaultMessage: SubscriptionStatus[subscription.status],
              })}
              {subscription.startTime && <> {new Date(subscription.startTime).toLocaleDateString()}</>}
            </>
          }
          hierarchy={statusHierarchy}
          shape={SHAPE.pill}
          color={statusColor}
        />
      );
    default:
      return (
        <>
          <Badge
            content={
              <>
                {intl.formatMessage({
                  id: `StripeSubscriptionStatus.${SubscriptionStatus[subscription.status]}`,
                  defaultMessage: SubscriptionStatus[subscription.status],
                })}
                <>
                  {subscription.startTime && hasFutureStartTime && (
                    <> {new Date(subscription.startTime).toLocaleDateString()}</>
                  )}
                </>
              </>
            }
            hierarchy={statusHierarchy}
            shape={SHAPE.pill}
            color={statusColor}
          />
          {isCancel && (
            <Badge
              content={
                <CancelContainer>
                  <b>
                    {" "}
                    {intl.formatMessage({
                      id: `StripeSubscriptionStatus.cancelAt`,
                      defaultMessage: "Cancel at",
                    })}
                  </b>{" "}
                  {subscription.cancelAt && <> {new Date(subscription.cancelAt).toLocaleDateString()}</>}
                </CancelContainer>
              }
              hierarchy={"secondary"}
              shape={SHAPE.pill}
              color={"primary"}
            />
          )}
        </>
      );
  }
};

const CancelContainer = styled("div", ({ $theme }) => ({
  display: "flex",
  gap: $theme.sizing.scale200,
}));

type EnumDictionary<T extends string | symbol | number, U> = {
  [K in T]: U;
};

const statusColors: EnumDictionary<SubscriptionStatus, any> = {
  [SubscriptionStatus.active]: COLOR.positive,
  [SubscriptionStatus.released]: COLOR.primary,
  [SubscriptionStatus.canceled]: COLOR.primary,
  [SubscriptionStatus.incomplete]: COLOR.warning,
  [SubscriptionStatus.incomplete_expired]: COLOR.primary,
  [SubscriptionStatus.past_due]: COLOR.negative,
  [SubscriptionStatus.trialing]: COLOR.accent,
  [SubscriptionStatus.unpaid]: COLOR.negative,
  [SubscriptionStatus.paused]: COLOR.primary,
  [SubscriptionStatus.not_started]: COLOR.primary,
};

const statusHierarchies: EnumDictionary<SubscriptionStatus, any> = {
  [SubscriptionStatus.active]: HIERARCHY.primary,
  [SubscriptionStatus.released]: HIERARCHY.primary,
  [SubscriptionStatus.canceled]: HIERARCHY.primary,
  [SubscriptionStatus.incomplete]: HIERARCHY.primary,
  [SubscriptionStatus.incomplete_expired]: HIERARCHY.primary,
  [SubscriptionStatus.past_due]: HIERARCHY.primary,
  [SubscriptionStatus.trialing]: HIERARCHY.primary,
  [SubscriptionStatus.unpaid]: HIERARCHY.primary,
  [SubscriptionStatus.paused]: HIERARCHY.primary,
  [SubscriptionStatus.not_started]: HIERARCHY.primary,
};
